// src/pages/OrdersPageModern.tsx - NOUVELLE INTERFACE MODERNE
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Layout, { cardClass } from '../components/Layout';
import ModernButton from '../components/ModernButton';
import PageWrapper from '../components/PageWrapper';
import PageLayout from '../components/PageLayout';
import OrderForm from '../components/OrderForm';
import OrderDetailModal from '../components/OrderDetailModal';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';

import { usePlanLimits } from '../hooks/usePlanLimits';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import {
  Plus, 
  Search, 
  X, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  DollarSign, 
  Calendar, 
  ShoppingCart, 
  Layers,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  User,
  Building2,
  MoreVertical,
  Edit,
  Eye,
  Trash2,
  Star,
  Activity,
  Target,
  Zap
} from 'lucide-react';
import toast from 'react-hot-toast';
import SubscriptionLimits from '../components/SubscriptionLimits';

type OrderRow = {
  id: string;
  title: string;
  budget: number | null;
  status: 'Pending' | 'In Progress' | 'Completed' | 'On Hold' | 'Cancelled' | string;
  due_date: string | null;
  created_at: string | null;
  clients: {
    name: string;
    platform: string | null;
  };
  description?: string | null;
  client_id?: string;
  start_date?: string | null;
  completed_date?: string | null;
  platform?: string | null;
  client_name?: string | null;
  client_email?: string | null;
  project_type?: string | null;
  priority_level?: string | null;
  estimated_hours?: number | null;
  hourly_rate?: number | null;
  payment_status?: string | null;
  notes?: string | null;
  tags?: string[] | null;
  revision_count?: number | null;
  client_feedback?: string | null;
};

const PAGE_SIZE = 12; // Reduced for card layout

// Status options for dropdown (matching database enum)
const ALL_STATUSES: Array<OrderRow['status']> = [
  'Pending',
  'In Progress',
  'Completed',
  'On Hold',
  'Cancelled'
];

const PageOrders: React.FC = () => {
  const { user } = useAuth();
  const { currency } = useCurrency();
  const { checkOrderLimit } = usePlanLimits();
  const { limits, subscription, isAdmin } = useSubscriptionPermissions();

  // Data
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Search (debounced)
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filters
  const [status, setStatus] = useState<string>('');
  const [platform, setPlatform] = useState<string>('');
  const [platformOptions, setPlatformOptions] = useState<string[]>([]);

  // View mode
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

  // Pagination
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Form modal
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OrderRow | null>(null);

  // Detail modal
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderRow | null>(null);

  // Status dropdown state
  const [openStatusFor, setOpenStatusFor] = useState<string | null>(null);
  const statusMenuRef = useRef<HTMLDivElement | null>(null);

  // 🔥 NAVIGATION INSTANTANÉE - Plus de debounce, recherche immédiate
  useEffect(() => {
    setDebouncedSearch(search.trim());
  }, [search]);

  // Reset page on filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, platform]);

  // Load platform options
  useEffect(() => {
    const loadPlatforms = async () => {
      if (!user) return;
      if (!isSupabaseConfigured || !supabase) {
        setPlatformOptions(['Fiverr', 'Upwork', 'Direct']);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('orders')
          .select('platform')
          .eq('user_id', user.id)
          .not('platform', 'is', null);
        
        if (error) throw error;
        
        const platforms = [...new Set(data?.map(r => r.platform).filter(Boolean))];
        setPlatformOptions(platforms);
      } catch (err) {
        console.error('Error loading platforms:', err);
        setPlatformOptions(['Fiverr', 'Upwork', 'Direct']);
      }
    };
    loadPlatforms();
  }, [user]);

  // Load orders - extracted function to be called from anywhere
  const fetchOrders = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!isSupabaseConfigured || !supabase) {
        console.error('Supabase not configured - cannot fetch orders');
        setError('Database not configured');
        setOrders([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('orders')
        .select(`
          id, title, budget, status, due_date, created_at, description,
          client_id, start_date, completed_date, platform,
          clients!inner(name, platform)
        `, { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // Apply filters
      if (debouncedSearch) {
        query = query.or(`title.ilike.%${debouncedSearch}%,description.ilike.%${debouncedSearch}%`);
      }
      if (status) {
        query = query.eq('status', status);
      }
      if (platform) {
        query = query.eq('platform', platform);
      }

      // Apply pagination
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      // Convertir les statuts de la DB vers l'affichage utilisateur
      const getStatusForDisplay = (status: string) => {
        switch (status) {
          case 'pending': return 'Pending';
          case 'in_progress': return 'In Progress';
          case 'completed': return 'Completed';
          case 'cancelled': return 'Cancelled';
          case 'on_hold': return 'On Hold';
          default: return status;
        }
      };

      const transformed = (data || []).map((o: any) => ({
        ...o,
        status: getStatusForDisplay(o.status)
      }));

      setOrders(transformed);
      setTotal(count || 0);
    } catch (err: any) {
      console.error('Error loading orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [user, debouncedSearch, status, platform, page]);

  // Load orders effect
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Update selectedOrder when orders change (after status update, etc.)
  useEffect(() => {
    if (selectedOrder && orders.length > 0) {
      const updatedOrder = orders.find(o => o.id === selectedOrder.id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }
    }
  }, [orders, selectedOrder]);

  // Open create form
  const openCreate = async () => {
    const canCreate = await checkOrderLimit();
    if (!canCreate) return;
    
    setEditingOrder(null);
    setIsFormOpen(true);
  };

  // Open edit form
  const openEdit = (order: OrderRow) => {
    setEditingOrder(order);
    setIsFormOpen(true);
  };

  // Open detail modal
  const openDetail = (order: OrderRow) => {
    setSelectedOrder(order);
    setIsDetailModalOpen(true);
  };

  // Handle form success
  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingOrder(null);
    // Reload orders
    window.location.reload();
  };

  // Get status color and icon
  const getStatusInfo = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return { 
          color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
          icon: <CheckCircle className="w-4 h-4" />
        };
      case 'in progress':
        return { 
          color: 'bg-blue-100 text-[rgb(var(--brand))] dark:bg-blue-900/30 dark:text-[rgb(var(--brand))]',
          icon: <Activity className="w-4 h-4" />
        };
      case 'pending':
        return { 
          color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
          icon: <Clock className="w-4 h-4" />
        };
      case 'on hold':
        return { 
          color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
          icon: <AlertCircle className="w-4 h-4" />
        };
      case 'cancelled':
        return { 
          color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
          icon: <X className="w-4 h-4" />
        };
      case 'awaiting payment':
        return { 
          color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
          icon: <DollarSign className="w-4 h-4" />
        };
      case 'in review':
        return { 
          color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
          icon: <Eye className="w-4 h-4" />
        };
      default:
        return { 
          color: 'bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-[rgb(var(--muted))]',
          icon: <AlertCircle className="w-4 h-4" />
        };
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string | null) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-[rgb(var(--muted))]';
    }
  };

  // Format currency
  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency 
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate days until due
  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Status dropdown handlers
  const toggleStatusMenu = (e: React.MouseEvent, orderId: string) => {
    e.stopPropagation();
    setOpenStatusFor(prev => (prev === orderId ? null : orderId));
  };

  const updateOrderStatus = async (orderId: string, newStatus: OrderRow['status']) => {
    // Convertir le statut pour la base de données
    const getStatusForDB = (status: string) => {
      switch (status) {
        case 'Pending': return 'pending';
        case 'In Progress': return 'in_progress';
        case 'Completed': return 'completed';
        case 'Cancelled': return 'cancelled';
        case 'On Hold': return 'on_hold';
        default: return 'pending'; // Valeur par défaut si non reconnu
      }
    };

    try {
      if (!isSupabaseConfigured || !supabase) {
        toast.error('Database not configured');
        return;
      }

      console.log('🔄 Updating order status:', { orderId, newStatus, dbStatus: getStatusForDB(newStatus) });

      const { error, data } = await supabase
        .from('orders')
        .update({ status: getStatusForDB(newStatus) })
        .eq('id', orderId)
        .select();

      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }

      console.log('✅ Status updated successfully:', data);
      toast.success(`Status updated to ${newStatus}`);
      
      // Recharger toutes les données pour mettre à jour preview, edit, etc.
      await fetchOrders();
    } catch (e: any) {
      console.error('❌ Failed to update status:', e);
      toast.error(`Failed to update status: ${e.message || e}`);
    }
  };

  const selectStatus = (e: React.MouseEvent, order: OrderRow, newStatus: OrderRow['status']) => {
    e.stopPropagation();
    setOpenStatusFor(null);
    if (order.status === newStatus) return;
    
    updateOrderStatus(order.id, newStatus);
  };

  // Close dropdown on outside click or Esc
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (statusMenuRef.current && statusMenuRef.current.contains(e.target as Node)) {
        return;
      }
      setOpenStatusFor(null);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenStatusFor(null);
    };
    if (openStatusFor) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKey);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openStatusFor]);

  if (loading && !orders.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="ml-3 text-[rgb(var(--muted))]">Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-400 font-semibold">Error loading orders</p>
        <p className="text-sm text-[rgb(var(--muted))] mt-1">{error}</p>
        <ModernButton onClick={() => window.location.reload()} className="mt-4">
          Retry
        </ModernButton>
      </div>
    );
  }

  // Vérifier si l'utilisateur est sur le plan gratuit
  const isFreePlan = !isAdmin && (subscription?.plan_name?.toLowerCase() === 'launch' || subscription?.plan_name?.toLowerCase() === 'lunch' || !subscription);
  const maxOrdersFree = 10;
  const currentLimit = limits?.maxOrders || maxOrdersFree;
  const showLimitWarning = isFreePlan && currentLimit !== -1;

  return (
    <PageWrapper>
      <PageLayout title="Orders">
        {/* Limit reminder for free plan */}
        {showLimitWarning && (
          <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-[rgb(var(--brand))] dark:text-[rgb(var(--brand))]">
              <span className="font-medium">Free plan limit:</span> You can create up to {maxOrdersFree} orders per month. Upgrade to Boost for unlimited orders.
            </p>
          </div>
        )}
        
      {/* KPIs row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 flex-shrink-0">
        <div className={`${cardClass} p-4 border-2 shadow-[0_2px_8px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[rgb(var(--muted))]">Total Orders</p>
              <p className="text-2xl font-bold text-[rgb(var(--fg))]">{total}</p>
            </div>
            <Package className="w-8 h-8 text-[rgb(var(--brand))]" />
          </div>
        </div>
        
        <div className={`${cardClass} p-4 border-2 shadow-[0_2px_8px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[rgb(var(--muted))]">In Progress</p>
              <p className="text-2xl font-bold text-[rgb(var(--fg))]">
                {orders?.filter(o => o.status === 'In Progress').length || 0}
              </p>
            </div>
            <Activity className="w-8 h-8 text-[rgb(var(--brand))]" />
          </div>
        </div>
        
        <div className={`${cardClass} p-4 border-2 shadow-[0_2px_8px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[rgb(var(--muted))]">Completed</p>
              <p className="text-2xl font-bold text-[rgb(var(--fg))]">
                {orders?.filter(o => o.status === 'Completed').length || 0}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className={`${cardClass} p-4 border-2 shadow-[0_2px_8px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[rgb(var(--muted))]">Total Revenue</p>
              <p className="text-2xl font-bold text-[rgb(var(--fg))]">
                {formatCurrency(orders?.reduce((sum, order) => sum + (order.budget || 0), 0) || 0)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Toolbar (search/filters/view switch) */}
      <div className="mt-4 md:mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
        <div className="md:col-span-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[rgb(var(--muted))]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search orders..."
              className="w-full pl-9 pr-9 py-2.5 rounded-xl theme-input theme-text-primary placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-[#007acc] focus:border-[#007acc]"
              type="text"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:theme-hover-button"
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-[rgb(var(--muted))]" />
              </button>
            )}
          </div>
        </div>
        <div className="md:col-span-4 flex items-center justify-end gap-2">
          {/* View Mode Toggle */}
          <div className="flex theme-card-bg theme-card-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'cards' 
                  ? 'bg-blue-600 text-[rgb(var(--fg))]' 
                  : 'text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))]'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list' 
                  ? 'bg-blue-600 text-[rgb(var(--fg))]' 
                  : 'text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))]'
              }`}
            >
              List
            </button>
          </div>
          <ModernButton onClick={openCreate} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Order
          </ModernButton>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 md:mt-6 flex-shrink-0">
        <div className="flex items-center gap-2 mb-3 text-[rgb(var(--muted))]">
          <Filter className="h-4 w-4" />
          <span className="text-sm font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2.5 rounded-xl theme-input theme-text-primary focus:ring-2 focus:ring-[#007acc]"
          >
            <option value="">Status (all)</option>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>

          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="px-3 py-2.5 rounded-xl theme-input theme-text-primary focus:ring-2 focus:ring-[#007acc]"
          >
            <option value="">Platform (all)</option>
            {platformOptions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Order cards grid */}
      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:flex-1 lg:content-start">
          {orders?.map((order) => {
            const statusInfo = getStatusInfo(order.status);
            const daysUntilDue = getDaysUntilDue(order.due_date);
            const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
            const isDueSoon = daysUntilDue !== null && daysUntilDue <= 3 && daysUntilDue >= 0;

            return (
              <div key={order.id} className={`${cardClass} p-4 hover:theme-hover-card transition-all duration-200 group border-2 shadow-[0_4px_12px_rgba(0,0,0,0.15)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)]`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[rgb(var(--fg))] text-sm mb-1 line-clamp-2">{order.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-[rgb(var(--muted))]">
                      <User className="w-3 h-3" />
                      <span>{order.clients.name}</span>
                    </div>
                  </div>
                  <div className="relative">
                    <button className="p-1 rounded-lg hover:theme-hover-button opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreVertical className="w-4 h-4 text-[rgb(var(--muted))]" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-[rgb(var(--fg))]">{formatCurrency(order.budget)}</span>
                    <div className="relative" ref={openStatusFor === order.id ? statusMenuRef : undefined}>
                      <button
                        onClick={(e) => toggleStatusMenu(e, order.id)}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color} hover:opacity-90 transition cursor-pointer`}
                        title="Change status"
                      >
                        {statusInfo.icon}
                        {order.status}
                      </button>
                      {openStatusFor === order.id && (
                        <div className="absolute z-20 right-0 mt-2 w-44 rounded-xl theme-card-border theme-card-bg shadow-lg">
                          <ul className="py-1 max-h-64 overflow-auto">
                            {ALL_STATUSES.map((st) => {
                              const statusInfo = getStatusInfo(st);
                              return (
                                <li key={st}>
                                  <button
                                    className={`w-full text-left px-3 py-2 text-sm hover:theme-hover-button ${
                                      st === order.status ? 'text-[rgb(var(--fg))]' : 'text-[rgb(var(--muted))]'
                                    }`}
                                    onClick={(e) => selectStatus(e, order, st)}
                                  >
                                    <span className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${
                                      statusInfo.color.includes('green') ? 'bg-green-500' : 
                                      statusInfo.color.includes('blue') ? 'bg-blue-500' : 
                                      statusInfo.color.includes('yellow') ? 'bg-yellow-500' : 
                                      statusInfo.color.includes('red') ? 'bg-red-500' : 
                                      statusInfo.color.includes('orange') ? 'bg-orange-500' : 
                                      statusInfo.color.includes('indigo') ? 'bg-indigo-500' : 
                                      statusInfo.color.includes('amber') ? 'bg-amber-500' : 
                                      'bg-slate-500'
                                    }`}></span>
                                    {st}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {order.due_date && (
                    <div className={`flex items-center gap-2 text-xs ${
                      isOverdue ? 'text-red-400' : 
                      isDueSoon ? 'text-yellow-400' : 
                      'text-[rgb(var(--muted))]'
                    }`}>
                      <Calendar className="w-3 h-3" />
                      <span>
                        Due: {formatDate(order.due_date)}
                        {daysUntilDue !== null && (
                          <span className="ml-1">
                            ({daysUntilDue > 0 ? `${daysUntilDue}d left` : `${Math.abs(daysUntilDue)}d overdue`})
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {order.platform && (
                    <div className="flex items-center gap-2 text-xs text-[rgb(var(--muted))]">
                      <Building2 className="w-3 h-3" />
                      <span>{order.platform}</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t theme-card-border flex items-center gap-2">
                  <button
                    onClick={() => openDetail(order)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-[rgb(var(--brand))] hover:text-[rgb(var(--brand))] hover:bg-blue-500/10 rounded-lg transition-colors"
                  >
                    <Eye className="w-3 h-3 inline mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => openEdit(order)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] hover:bg-gray-500/10 rounded-lg transition-colors"
                  >
                    <Edit className="w-3 h-3 inline mr-1" />
                    Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`${cardClass} p-0 overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="theme-card-bg">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[rgb(var(--muted))] uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[rgb(var(--muted))] uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[rgb(var(--muted))] uppercase tracking-wider">Budget</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[rgb(var(--muted))] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[rgb(var(--muted))] uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[rgb(var(--muted))] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1C2230]">
                {orders?.map((order) => {
                  const statusInfo = getStatusInfo(order.status);
                  const daysUntilDue = getDaysUntilDue(order.due_date);
                  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
                  const isDueSoon = daysUntilDue !== null && daysUntilDue <= 3 && daysUntilDue >= 0;

                  return (
                    <tr key={order.id} className="hover:theme-hover-card transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-[rgb(var(--fg))]">{order.title}</div>
                          {order.platform && (
                            <div className="text-sm text-[rgb(var(--muted))]">{order.platform}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-[rgb(var(--muted))]">{order.clients.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-[rgb(var(--fg))]">{formatCurrency(order.budget)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap relative" ref={openStatusFor === order.id ? statusMenuRef : undefined}>
                        <button
                          type="button"
                          onClick={(e) => toggleStatusMenu(e, order.id)}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color} hover:opacity-90 transition cursor-pointer`}
                          title="Change status"
                        >
                          {statusInfo.icon}
                          {order.status}
                        </button>
                        {openStatusFor === order.id && (
                          <div className="absolute z-20 mt-2 w-44 rounded-xl theme-card-border theme-card-bg shadow-lg">
                            <ul className="py-1 max-h-64 overflow-auto">
                              {ALL_STATUSES.map((st) => {
                                const statusInfo = getStatusInfo(st);
                                return (
                                  <li key={st}>
                                    <button
                                      className={`w-full text-left px-3 py-2 text-sm hover:theme-hover-button ${
                                        st === order.status ? 'text-[rgb(var(--fg))]' : 'text-[rgb(var(--muted))]'
                                      }`}
                                      onClick={(e) => selectStatus(e, order, st)}
                                    >
                                      <span className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${
                                        statusInfo.color.includes('green') ? 'bg-green-500' : 
                                        statusInfo.color.includes('blue') ? 'bg-blue-500' : 
                                        statusInfo.color.includes('yellow') ? 'bg-yellow-500' : 
                                        statusInfo.color.includes('red') ? 'bg-red-500' : 
                                        statusInfo.color.includes('orange') ? 'bg-orange-500' : 
                                        statusInfo.color.includes('indigo') ? 'bg-indigo-500' : 
                                        statusInfo.color.includes('amber') ? 'bg-amber-500' : 
                                        'bg-slate-500'
                                      }`}></span>
                                      {st}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${
                          isOverdue ? 'text-red-400' : 
                          isDueSoon ? 'text-yellow-400' : 
                          'text-[rgb(var(--muted))]'
                        }`}>
                          {formatDate(order.due_date)}
                          {daysUntilDue !== null && (
                            <div className="text-xs">
                              {daysUntilDue > 0 ? `${daysUntilDue}d left` : `${Math.abs(daysUntilDue)}d overdue`}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openDetail(order)}
                            className="text-[rgb(var(--brand))] hover:text-[rgb(var(--brand))] p-1 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEdit(order)}
                            className="text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] p-1 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={`${cardClass} p-4 border-2 shadow-[0_2px_8px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]`}>
          <div className="flex items-center justify-between">
            <div className="text-sm text-[rgb(var(--muted))]">
              Showing {((page - 1) * PAGE_SIZE) + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} orders
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg theme-card-border theme-card-bg theme-text-secondary hover:theme-text-primary hover:theme-hover-button disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-2 text-sm text-[rgb(var(--muted))]">
                {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg theme-card-border theme-card-bg theme-text-secondary hover:theme-text-primary hover:theme-hover-button disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <OrderForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
        order={editingOrder}
      />

      <OrderDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        order={selectedOrder}
      />
      </PageLayout>
    </PageWrapper>
  );
};

export default PageOrders;
